/* eslint-disable max-lines, max-depth, max-depth, max-statements, complexity */
// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapActions, mapState } from "vuex";
import { markRaw } from "vue";
import CommonError from '@/common/nodejs/errors';

import api from "../../../common/api";
import alert from "../../../share/alert";
import loading from "../../../share/components/vue/ProcessingIcon";
import WrappedTeleport from "../../../share/components/vue/WrappedTeleport";
import config from "../../../share/config";
import session from "../../../share/session";
import { POLLING_ERROR_POST_INVALID_RESPONSE } from "../../../share/util/api/polling";
import individualDiagnose from "../../../share/util/individualDiagnose";
import diagnose from "../../../share/util/diagnose";
import util from "../../../share/util/utils";
import dataMonitorExecute from "../../vue/dataMonitor/DataMonitorMeasure";
import dataMonitorSelect from "../../vue/dataMonitor/DataMonitorSelect";
import error from '../../../common/nodejs/errors';
import logWrapper from "../../../share/logWrapper";
import {
  WORKER, WORKER_TASK, WORKER_EVENT, DATA_STORAGE, Cache, getItemsBySystem, getNoSystemItemId, setSystem,
  getDid, splitSystemAndItemId, getTranslateValue, TYPE_CALLER, getSystem, isPhase6At
} from './dataMonitorUtil';
import { IndexedDbManager, STORE_NAME, DATA_POINT_STORE } from './indexedDbManager';
import { fixNumberWithDecimal } from "../../../common/nodejs/mathFunctions";

const { v4: uuid } = require("uuid");

const ERR_MSG_KEY = {
  INFO_GET: "get",
  INIT: "init",
  MEASURE_START: "start",
  MEASURE_STOP: "stop",
  COMMUNICATION: "communication",
  INTDASH: "intdash",
  REQUESTS_START: "requests_start",
  FETCH: "fetch",
  REQUESTS_EXECUTION: "requests_execution",
  MEASUREMENT_LIMIT: "measurement_limit",
};

// ポーリング間隔（ミリ秒）
const INTERVAL = {
  MEASURE_SLEEP: 14000, // Polling interval sleep（Completed確認後）共通関数のインターバルと合わせて15秒停止
  WATCH_TIMER: 10000, // 計測時間監視インターバル(ms)
  WAIT_TIME: 1000, // データモニタ開始のPollingのCompletedまでPollingの待ち時間
  WORKER_SLEEP: 500, // Workerの待機時間
  NETWORK_ERROR: 5000, // APIにてネットワークエラーなどの予期せぬエラー発生時のリトライインターバル
};

// API失敗時の再実行回数
const RETRY = {
  MEASURE_START: Number.MAX_VALUE, // データ総件数の上限で計測を停止するため、上限は設けない
  MEASURE_STOP: Number.MAX_VALUE,
  MEASURE_STOP_NETWORK_ERROR: 12, // 停止APIにてネットワークエラー発生時のリトライ回数
  MEASURE_POLLING_NETWORK_ERROR: 4 // ポーリング中にてネットワークエラー発生時のリトライ回数
};

// 最大連続計測時間(msec)
const MAX_TIME = 3600 * 1000;

// データ総件数の上限(2000/秒 × 3780秒。最大実行時間は1時間だが、3分ほどデータ数は多めにしておく)
const DATA_TOTAL_COUNT_LIMIT = 2000 * 3780;

// intdashサーバのプロジェクトUUID
const INTDASH_PROJECT_UUID = "00000000-0000-0000-0000-000000000000";

export default {
  name: "dataMonitor",
  data() {
    return {
      processing: false,
      stopProcessing: false,
      checkProcessing: false,
      // 画面に表示する為の機能リスト
      filterFunctionList: [],
      showDataMonitorExecute: false,
      // データモニタ用のi18nKey
      i18nDmKey: "",
      menuResponse: null,
      conversionList: {},
      // 変換情報を早く引くため、DIDごとのインデックスを持つオブジェクト
      didConversionMap: markRaw({}),
      dataMonitorId: "",
      // 選択項目
      selectedFuncs: [],
      selectedFunctionHash: {},
      // グラフ設定
      graphConfigInfo: {
        xRange: 0,
        yRange: {},
        lineSetting: {}
      },
      completeResolve: () => { },

      // 以下API
      //モニタ項目
      monitorResponse: null,
      // 項目一覧データ	: モニター対象の項目一覧を保持する。
      //ECU固有情報の項目名
      // ECU固有情報の説明
      ecuItems: {
        ecuItem: "",
        ecuDescription: "",
      },
      // 項目一覧データ（表示用）: ④にフィルター後の項目一覧データを送る。
      //ECU固有情報の項目名
      // ECU固有情報の説明
      ecuDispItems: {
        ecuItem: "",
        ecuDescription: "",
      },
      // 項目一覧データ（選択用）: ⑤に項目一覧で選択されたデータを送る。
      //ECU固有情報の項目名
      // ECU固有情報の説明
      ecuSelectItems: {
        ecuItem: "",
        ecuDescription: "",
      },

      // intdashサーバ計測UUID
      measurementUuid: "",
      // 計測マーカーUUID
      // markerUuid: "",
      // Intdashの接続先
      endpoint: "",
      // APIトークン
      apiToken: "",
      // edge ID
      edgeId: "",
      // トークン有効期限
      tkExpire: "",
      // リクエストID
      requestId: "",
      // メモAPI用 uuid
      memoUuid: "",

      // API 取得確認 FLG
      isGetApi: false,
      // 計測開始 API 取得成功 FLG
      isCompleted: false,
      // Intdashとの接続完了 FLG
      isConnected: false,
      // Polling中 判定 FLG
      isPolling: true,
      // ポーリング停止指示
      hasStopOrder: false,
      // 計測開始時間(ナノ秒単位のUNIXタイムスタンプ)。ただしミリ秒以下は0になるように調整
      baseTime: null,
      // 計測開始時間のミリ秒以下が0となるように、調整した時間
      baseTimeOffset: null,
      // エラーフラグ
      isError: false,
      // サインアウト
      signout: false,

      // Workerエラーフラグ
      workerError: false,
      // マーカー情報
      markerInfo: markRaw([]),
      // 項目ごとのデータ（単位、平均、最大最小など）
      funcValues: {},
      // Web workerのインスタンス
      workerInstance: markRaw({}),
      // 対象ECUのID。routeのStateから取得できない再生画面でのみ使用する(OBD互換用でUDSデータモニタは使用しない)
      specifiedSystemId: "",
      // (データ再生)AT駆動中区間のリスト
      atDrivingIntervals: [],
      // データ再生からの呼び出しか否か
      isDataRecollection: false,
      // systemId
      systemId: "",
      // 子コンポーネントのMeasureを明示的に消す
      unmountMeasure: false,
      // HEV系AT対応：テストケースメニュータイトルキー
      hevAtMenuTitleKey: "",
      // RestfulWorkerで除外されたデータ
      escapedDataPoints: {},
      // indexedDBを管理するクラスのインスタンス
      indexedDbManager: null,
    };
  },

  props: {
    // 項目選択画面表示フラグ
    showSelect: { type: Boolean, default: true, required: true },
    // 全画面モーダルの表示有無
    showModal: { type: Boolean, default: false, required: false },
    // 計測中フラグ
    inExecution: { type: Boolean, default: false, required: true },
    // 計測データ再生中フラグ
    inPlaying: { type: Boolean, default: false, required: true },
    // システム間移動判定
    isUseCache: undefined,
    // 親画面名称
    parentName: { type: String, default: "data_monitor", required: false },
    // 特殊機能判定
    isSpecial: { type: Boolean, default: false, required: false },
    // ActiveTestの強調表示項目リスト
    highlightItems: { type: Array, 'default': () => ([]), required: false },
    // DM機能の設定インスタンス
    dataMonitorFunctionSettings: { type: Object, required: true },
    // アクティブテストの選択した機能要求ID
    selectedActiveTestId: { type: String, default: undefined, required: false },
    // 選択したアクティブテストの情報
    selectedAtFunc: { type: Object, default: {}, required: false },
    // アクティブテスト駆動状態
    isActiveTestDriving: { type: Boolean, default: false, required: false },
    // （データ再生）アクティブテストIO/Routine Control対象の情報
    recollectionAtInfo: { type: Array, 'default': () => ([]), required: false },
    // （データ再生）アクティブテスト駆動状態一覧
    atOperatingStatusList: { type: Array, 'default': () => ([]), required: false },
    // （データ再生）Phase6対象HEV系ATか否か
    isPhase6AtRecollection: { type: Boolean, 'default': false, required: false },
    // 計測画面全体をteleportさせるモーダルダイアログのID
    modalContentId: { type: String, default: undefined, required: false },
    // グラフ設定ボタンなどをteleportさせる行先のDOMのID
    graphSettingsDestinationId: { type: String, default: undefined, required: false },
    // (保存データ一覧) CSVの出力のみの利用か
    isOnlyCsvExport: { type: Boolean, default: false, required: false },
  },

  components: {
    "processing-view": loading,
    "data-monitor-execute": dataMonitorExecute,
    "data-monitor-select": dataMonitorSelect,
    "wrapped-teleport": WrappedTeleport
  },

  watch: {
    showSelect: {
      handler(val) {
        if (val) {
          this.clearMeasuredData();
        }
      },
    },
    baseTime: {
      handler(val) {
        this.$emit('base-time-changed', val);
      },
    },
    isCompleted: {
      handler(val) {
        // 計測開始API取得完了かつIntdashとの接続完了の場合、loading解除
        if (val && this.isConnected) {
          this.processing = false;
        }
      },
    },
    isConnected: {
      handler(val) {
        // 計測開始API取得完了かつIntdashとの接続完了の場合、loading解除
        if (val && this.isCompleted) {
          this.processing = false;
        }
      },
    },
  },

  computed: {
    ...mapState({
      language: (state) => state.systems.language,
      netAppId: (state) => state.netAppConnector.pairingAppId,
      userProperty: (state) => state.userProperty,
      individualMenus: (state) => state.vehicle.individualMenus,
      projectDetails: (state) => state.cache.projectDetails,
      isTestModeState: (state) => state.netAppConnector.isTestMode,
      cachedProjectDataSource: state => state.cache.projectDataSource,
    }),
    selectedDidList() {
      const res = this.selectedFuncs.map((func) => func.data_monitor_id);
      return res;
    },

    itemComparator() {
      if (this.sortAsc) {
        return (a, b) => a.sort_id - b.sort_id;
      } else {
        return (a, b) => b.sort_id - a.sort_id;
      }
    },
    // DMAT判定
    isDataMonitor() {
      return this.parentName === "data_monitor";
    },
    isDisplayProcessing() {
      return this.processing || this.stopProcessing
        // 計測終了後もcheckGetApiResponseをリトライしている場合
        || (!this.inExecution && this.checkProcessing);
    },
    isActiveTest() {
      return this.dataMonitorFunctionSettings.getTypeCaller() === TYPE_CALLER.AT;
    },
    // HEV系ATのDM表示に用いる
    isHev() { return this.atInfo && (this.atInfo.length > 0); },
    atInfo() { return this.isDataRecollection ? this.recollectionAtInfo : this.selectedAtFunc?.at_info; },
    // データ再生画面の場合はisTestModeをfalseにする
    isTestMode() { return this.isTestModeState && !this.dataMonitorFunctionSettings.isPlay(); },
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),
    ...mapActions("observation", ["usageCountUp"]),
    ...mapActions("app", ["setViewProcessMode"]),

    /**
     * componentのmounted時に呼び出す処理
     */
    mountedLogic() {
    },

    /**
     * 子コンポーネントのDataMonitorMeasureのMount・Unmountを親コンポーネントに通知する
     * @param {boolean} isMounted Mountされたか否か
     */
    measureMountedEvent(isMounted) {
      this.$emit("measure-mounted", isMounted);
    },

    /**
     * 処理中の表示
     * @param {*} flg 表示するか否か
     */
    displayProcessing(flg) {
      this.processing = flg;
    },

    /** 計測結果を破棄 */
    clearMeasuredData() {
      this.dataMonitorList = [];
      this.baseTime = null;
      this.baseTimeOffset = null;
      this.funcValues = {};
      this.escapedDataPoints = {};
    },

    /** 計測開始（初回） */
    startExecute(selectedFunctionList = []) {
      // 初回の計測開始時に、全画面モーダルを自動表示する
      this.$emit("maximum");
      // 計測画面へ遷移
      this.$emit("toggle-show-select", false);
      // 選択項目を取得
      this.conversionList = {};
      const selectedFuncsHash = {};
      const highlightList = [];
      for (const selectedFunction of selectedFunctionList) {
        const { data_monitor_id, ...conv } = selectedFunction;
        selectedFuncsHash[data_monitor_id] = conv;
        if (this.highlightItems.includes(selectedFunction.data_monitor_id)) {
          highlightList.push(selectedFunction);
        }
      }
      const highlightFunctions = selectedFunctionList.filter(_function => highlightList.includes(_function));
      const otherFunctions = selectedFunctionList.filter(_function => !highlightList.includes(_function));
      this.selectedFuncs = highlightFunctions.concat(otherFunctions);
      this.selectedFunctionHash = selectedFuncsHash;
      if (this.isDataMonitor || selectedFunctionList.length > 0) {
        this.startMeasureApi();
      }
    },

    /** モニタ開始 */
    startMonitor() {
      this.startMeasureApi();
    },

    /**
     * モニタ停止
     * @param {string} activeTestId アクティブテストID(機能要求ID)
     * @param {boolean} errorTerminateFlg errorTerminateFlgフラグ(trueの場合typeをerrorTerminateにする)
     */
    async stopMonitor(activeTestId = undefined, errorTerminateFlg = false) {
      // 停止の成否に関わらず、停止実行時点でツリーは戻す。ただしアクティブテストは駆動との関連があるため、アクティブテスト内コンポーネントで切り替え。
      !this.isActiveTest && this.setViewProcessMode(config.PROCESS_IDLING);
      try {
        this.stopProcessing = true;
        // 引数未指定で親から機能要求IDを渡されていた場合は、そちらを使用する
        const selectedActiveTestId = activeTestId ? activeTestId : this.selectedActiveTestId;
        await this.measureStop(this.selectedDidList, errorTerminateFlg, selectedActiveTestId);
      } finally {
        this.stopProcessing = false;
      }
      const measuredId = this.memoUuid ? this.memoUuid.toString() : "";

      this.$emit("change-measure-id", measuredId);
    },

    /**
     * モニタ停止(errorTerminateで停止)]
     * @param {string} activeTestId アクティブテストID(機能要求ID)
     */
    async stopMonitorErrorTerminate(activeTestId = undefined) {
      this.stopMonitor(activeTestId, true);
    },

    /** データ件数の通知 */
    notifyDataTotalCount(count) {
      // 件数上限オーバーの場合。ただし再生画面ではエラーを出さない
      if (this.isPolling && count > DATA_TOTAL_COUNT_LIMIT && !this.dataMonitorFunctionSettings.isPlay()) {
        this.isPolling = false;

        this.$emit("dm-err-notify", this.$t("individual.alert_data_monitor_data_limit_exceeded"), true);
        if (this.isActiveTest) {
          // アクティブテストの場合、個別のエラーハンドリングを行うため、ここでデータモニタの停止やエラーダイアログ表示しない
          return;
        }

        // 計測API停止
        this.stopMonitor();
        alert.showWarning(
          this,
          null,
          this.$t("individual.alert_data_monitor_data_limit_exceeded"),
          () => { }
        );
      }
    },

    /** 計測データ再生/停止状態切替 */
    togglePlaying(flg) {
      this.$emit("toggle-playing", flg);
    },

    /** 選択項目の言語変更データ受取 */
    changeSelectedFuncs(list) {
      const selectedFuncsHash = {};
      for (const selectedFunction of list) {
        const { data_monitor_id, ...conv } = selectedFunction;
        selectedFuncsHash[data_monitor_id] = conv;
      }
      this.selectedFuncs = list;
      this.selectedFunctionHash = selectedFuncsHash;
    },

    /** グラフ設定情報を保持 */
    setGraphConfig(xRange, yRange, lineSetting) {
      this.graphConfigInfo.xRange = xRange;
      // yRangeに項目が存在していれば更新、存在しなければ追加、設定済みの項目はそのまま
      Object.keys(yRange).forEach((did) => {
        this.graphConfigInfo.yRange[did] = yRange[did];
      });

      // lineSettingに項目が存在していれば更新、存在しなければ追加、設定済みの項目はそのまま
      if (lineSetting) {
        Object.keys(lineSetting).forEach((did) => {
          this.graphConfigInfo.lineSetting[did] = lineSetting[did];
        });
      }
    },

    /** グラフ項目数を設定 */
    changeCheckValue(checkLines) {
      this.$emit("change-check-value", checkLines);
    },

    /**
     * 現在タブの変更を親コンポーネントへ通知
     * @param {string} currentTab 現在のタブ
     */
    currentTabChanged(currentTab) {
      this.$emit("current-tab-changed", currentTab);
    },

    /**
     * ズーム/パン状態の変更を親コンポーネントへ通知
     * @param {boolean} isZoomPanActive ズーム/パン状態か否か
     */
    zoomPanStateChanged(isZoomPanActive) {
      this.$emit("zoom-pan-state-changed", isZoomPanActive);
    },

    /** 計測画面の範囲を取得 */
    getAreaStyle() {
      let className = this.showModal ? "dm-area-modal" : "dm-area";
      if (this.parentName === "active_test") {
        className = this.showModal ? "dm-area-at-modal" : "dm-area-at";
      } else if (this.parentName === "data_recollection") {
        className = this.showModal ? "dm-area-modal" : "dm-area-rc";
      }
      return {
        [className]: true,
      };
    },

    /**
     * 子コンポーネントから計測データを取得する
     * @returns 計測データ
     */
    getMeasureData() {
      return this.$refs.data_monitor_execute.convertedData;
    },

    /**
     * 子コンポーネントから未変換データを取得する
     */
    getDebugOriginData() {
      return this.$refs.data_monitor_execute.debugOriginData;
    },

    /**
     * タイムラインコンポーネントからマーカー情報を取得する
     * @returns マーカー情報
     */
    getMarkers() {
      return this.$refs.data_monitor_execute.getMarkers();
    },

    /**
     * 子コンポーネントから描画間隔を取得する
     * @returns {number} 描画間隔
     */
    getDrawInterval() {
      return this.$refs.data_monitor_execute.drawInterval;
    },

    /**
     * 計測開始API始動
     */
    async startMeasureApi() {
      // UDSデータモニタは単位や自由表記の値が言語によって異なるため、計測開始時に毎回キャッシュをクリアする
      new Cache().destroy();
      // 計測FLGを開始に変更
      this.$emit("toggle-execute", true);
      try {
        // 計測中はツリー非表示にする
        this.setViewProcessMode(config.CONTAINER_EXECUTING);
        // 処理中フラグONにし、計測開始API取得/Intdashとの接続完了 までローディングアイコンを出す
        this.processing = true;
        this.isError = false;

        const token = session.getToken();

        this.measurementUuid = uuid();
        this.isPolling = true;
        this.isGetApi = false;
        this.isCompleted = false;
        this.isConnected = false;

        const isSucceedIntdash = await this.setIntdashInfo();
        // intdashAccess情報取得失敗の場合は以降の処理は行わない。
        if (!isSucceedIntdash) {
          // ボタンの活性・非活性変更
          this.$emit("toggle-execute", false);
          return;
        }

        const isSucceedConversionList = await this.setConversionList(this.selectedDidList);
        // monitorConversion API失敗 かつ HEV系ATでない場合は以降の処理は行わない。
        if (!isSucceedConversionList && !this.isHev) {
          // ボタンの活性・非活性変更
          this.$emit("toggle-execute", false);
          return;
        }

        this.hasStopOrder = false;

        // iSCPダウンストリーム処理開始
        this.startDownstream();

        // データモニタ実行API
        this.measureStart(token, this.selectedDidList);

        // トレーニングモードではIntdashと接続しないためローディングアイコンは固定時間で消去
        if (this.isTestMode) {
          await util.sleep(1000);
          this.processing = false;
        }

        // iSCPダウンストリームの終了待ち
        while (this.isPolling && !this.hasStopOrder) {
          await util.sleep(INTERVAL.WAIT_TIME);
        }

      } finally {
        // 処理中フラグOFF
        this.processing = false;
        // 計測終了時はツリーは戻す。ただしアクティブテストは駆動との関連があるため、アクティブテスト内コンポーネントで切り替え。
        !this.isActiveTest && this.setViewProcessMode(config.PROCESS_IDLING);
      }
    },

    /**
     * Intdashへのアクセス情報を取得して保持する
     * @returns {boolean} 処理の成否
     */
    async setIntdashInfo() {
      const EXPIRY_MARGIN_SEC = 7200; // 2時間
      const expiredTime = new Date(this.tkExpire * 1000);
      const now = new Date();
      const isExpired = (expiredTime - now) / 1000 <= EXPIRY_MARGIN_SEC;

      // intdashAccess情報がない or tokenの有効期限が切れた場合
      if (
        this.apiToken === "" ||
        (!this.dataMonitorFunctionSettings.isPlay() && this.edgeId === "") ||
        this.tkExpire === "" ||
        this.endpoint === "" ||
        isExpired ||
        // 有効期限変更対応前のトークンの場合、トークンを取りなおす。
        expiredTime.getFullYear() - now.getFullYear() >= 1
      ) {
        // intdashAccess API
        await this.getIntdashAccess();
        // intdashAccess情報取得を実施しても情報が無い場合はintdashAccess情報取得失敗とする
        return (
          this.apiToken !== "" &&
          (this.dataMonitorFunctionSettings.isPlay() || this.edgeId !== "") &&
          this.tkExpire !== "" &&
          this.endpoint !== ""
        );
      }
      return true;
    },

    /**
     * 変換情報を取得して保持する
     * @param {array} didList 変換情報を取得するDIDのリスト
     * @param {string} projectId プロジェクトのID（再生画面でのみ使用）
     * @param {number} registeredAt 計測登録日時のepoch秒（再生画面でのみ使用）
     * @returns {boolean} 処理の成否
     */
    async setConversionList(didList, projectId = null, registeredAt = null) {
      // HEV系ATのDM１行目表示対象のうちRIDが含まれる場合は除外
      let modifiedDidList = didList;
      if (this.isHev) {
        modifiedDidList = modifiedDidList.filter(item => {
          let res = true;
          for (const element of this.atInfo) {
            if (getNoSystemItemId(item) === element.id) {
              res = false;
            }
          }
          return res;
        });
      }
      const itemsBySystem = getItemsBySystem(modifiedDidList);
      const targetDidListBySystem = {};
      for (const systemName of Object.keys(itemsBySystem)) {
        // 選択項目をAPI最大一括取得で切り出し処理
        targetDidListBySystem[systemName] = this.getDidListArr(itemsBySystem[systemName]);
      }

      // monitorConversion API
      const monitorConversionList = await this.getMonitorConversion(
        targetDidListBySystem, projectId, registeredAt
      );
      // monitorConversion APIのレスポンスサイズが1未満の場合はmonitorConversion API失敗とする
      if (Object.keys(monitorConversionList).length < 1) {
        return false;
      }
      this.conversionList = monitorConversionList;
      return true;
    },

    /**
     * データ再生を開始する
     * @param {object} measuredData 再生する項目
     * @param {string} specifiedSystemId 再生するECUのID(OBD互換用でUDSデータモニタは使用しない)
     * @param {string} projectId 再生するプロジェクトのID
     * @param {array} atInfo AT情報
     * @param {function} callback コールバック関数
     */
    async startPlayApi(measuredData, specifiedSystemId, projectId, atInfo = [], callback = () => { }) {
      // データ保存先。計測情報になければ古いデータのためintdash。
      const storage = measuredData.storage || DATA_STORAGE.INTDASH;
      this.systemId = specifiedSystemId;
      this.isDataRecollection = true;
      if (this.measurementUuid === measuredData.measurement_id) {
        // 言語変更時にデータの再取得を実施しない
        return;
      }
      this.measurementUuid = measuredData.measurement_id;

      // 再生FLGを開始に変更
      this.togglePlaying(true);

      let tmpSelectedFuncs = [];
      this.selectedFuncs = [];
      for (const systemName of Object.keys(measuredData.monitor_items)) {
        const region = individualDiagnose.getRegion(this, this.individualMenus, this.projectDetails, this.measurementUuid, systemName);
        const findFFDi18nKey = individualDiagnose.getFFDi18nKeyWithRegion(
          this, region, systemName
        );
        // HEV系AT対応：翻訳キーを独自に取得
        this.hevAtMenuTitleKey = this.dataMonitorFunctionSettings.isPlay() ?
          measuredData.item_name : `at${systemName}.${this.selectedAtFunc.active_test_id.slice(0, -2)}.menu_title`;
        const selectedItems = measuredData.monitor_items[systemName].map(monitorItem => {
          let i18nDmItemKey = `${findFFDi18nKey}.${getNoSystemItemId(monitorItem.id)}`;
          // HEV系AT対応：DM一行目に表示する項目の場合のmenu_titleキー
          if (atInfo.length !== 0) {
            for (const element of atInfo) {
              i18nDmItemKey = element.id === monitorItem.id ? this.hevAtMenuTitleKey : i18nDmItemKey;
            }
          }
          return {
            data_monitor_id: setSystem(systemName, monitorItem.id),
            menu_title: util.trimStr(this.$t(i18nDmItemKey)),
            display_order: monitorItem.display_order
          };
        });
        tmpSelectedFuncs = tmpSelectedFuncs.concat(selectedItems);
      }

      // 表示順でソートして選択項目にセット
      tmpSelectedFuncs.sort((a, b) => { return (a.display_order ?? 0) - (b.display_order ?? 0); });
      this.selectedFuncs = tmpSelectedFuncs;

      // 処理中フラグONにし、計測開始API取得/Intdashとの接続完了 までローディングアイコンを出す
      this.processing = true;
      this.isPolling = true;

      try {
        // データ保存先がintdashの場合のみ接続情報取得
        if (storage === DATA_STORAGE.INTDASH) {
          const isSucceedIntdash = await this.setIntdashInfo();
          // intdashAccess情報取得失敗の場合は以降の処理は行わない。
          if (!isSucceedIntdash) {
            // ボタンの活性・非活性変更
            this.togglePlaying(false);
            return;
          }
        } else {
          // indexedDB初期化
          this.indexedDbManager = new IndexedDbManager();
          try {
            await this.indexedDbManager.asyncOpenDB();
            await this.indexedDbManager.asyncClearStore(STORE_NAME.DATA_POINT);
          } catch {
            // ボタンの活性・非活性変更
            this.togglePlaying(false);
            return;
          }
        }

        const selectedDidList = this.selectedFuncs.map(item => item.data_monitor_id);
        const isSucceedConversionList = await this.setConversionList(selectedDidList, projectId, measuredData.registered_at);
        // monitorConversion API失敗の場合は以降の処理は行わない。
        if (!isSucceedConversionList) {
          // ボタンの活性・非活性変更
          this.togglePlaying(false);
          return;
        }

        this.hasStopOrder = false;
        // 再生データ取得中はローディングを消す
        this.processing = false;

        this.startRestfulPolling(measuredData.measurement_id, measuredData.registered_at, projectId, storage);

        // 再生データの取得の終了待ち
        while (this.isPolling && !this.hasStopOrder) {
          await util.sleep(INTERVAL.WAIT_TIME);
        }

      } finally {
        // 処理中フラグOFF
        this.processing = false;
        this.togglePlaying(false);
        // マーカーを反映完了させるため、描画が完全に完了するまで待つ
        await util.sleep(this.getDrawInterval());
        callback();
      }
    },

    /**
     * ダウンストリームの開始処理
     */
    async startDownstream() {
      const conversionKeys = Object.keys(this.conversionList);
      const conversionValues = Object.values(this.conversionList);
      const startMessage = {
        type: WORKER_TASK.START,
        api_token: this.apiToken,
        edge_id: this.edgeId,
        pj_uuid: INTDASH_PROJECT_UUID,
        address: this.endpoint,
        conversionKeys: JSON.parse(JSON.stringify(conversionKeys)),
        conversionValues: JSON.parse(JSON.stringify(conversionValues)),
        didConversionMap: JSON.parse(JSON.stringify(this.didConversionMap)),
        didSizes: JSON.parse(JSON.stringify(this.didSizes))
      };
      this.startWorker(WORKER.REALTIME, startMessage, this.iscpDownstreamWorkerListener);
    },

    /**
     * REST APIによるポーリングの開始処理
     * @param {string} measurementId 計測ID
     * @param {string} measuredId 計測登録日時
     * @param {string} projectId 再生するプロジェクトのID
     * @param {DATA_STORAGE} storage 計測データの保存先
     */
    async startRestfulPolling(measurementId, measuredId, projectId, storage) {
      const conversionKeys = Object.keys(this.conversionList);
      const conversionValues = Object.values(this.conversionList);
      const messageBase = {
        type: WORKER_TASK.START,
        measurement_id: measurementId,
        conversionKeys: JSON.parse(JSON.stringify(conversionKeys)),
        conversionValues: JSON.parse(JSON.stringify(conversionValues)),
        didConversionMap: JSON.parse(JSON.stringify(this.didConversionMap)),
        didSizes: JSON.parse(JSON.stringify(this.didSizes))
      };

      if (storage === DATA_STORAGE.S3) {
        const token = session.getToken();
        const loginToken = session.getLoginToken();
        const startMessage = {
          ...messageBase,
          api_token: token.id_token,
          session_token: loginToken.session_token,
          project_id: projectId,
          measuredId: measuredId,
          cachedProjectDataSource: this.cachedProjectDataSource
        };
        this.startWorker(WORKER.SSM5_RESTFUL, startMessage, this.restfulWorkerListener);
      } else {
        const startMessage = {
          ...messageBase,
          api_token: this.apiToken,
          pj_uuid: INTDASH_PROJECT_UUID,
          address: this.endpoint,
        };
        this.startWorker(WORKER.RESTFUL, startMessage, this.restfulWorkerListener);
      }
    },

    /**
     * Web workerの開始処理
     * @param {string} workerType 開始するWebWorkerの種別
     * @param {object} startMessage Workerに開始を通知するメッセージ
     * @param {function} listenerCallback Workerからのメッセージを受け取る関数
     */
    async startWorker(workerType, startMessage, listenerCallback) {
      // Workerを作成
      if (!(this.workerInstance instanceof Worker)) {
        // webpackChunkNameを設定した"data-monitor-worker"が、ビルド時のファイル名に使用される
        this.workerInstance = new Worker(
          /* webpackChunkName: "data-monitor-worker" */ new URL('./DataMonitorWorker.js', import.meta.url)
        );
      }

      // Workerに処理開始メッセージを送信
      this.workerInstance.postMessage({ ...startMessage, worker: workerType, isTestMode: this.isTestMode });

      // Workerエラーフラグのリセット
      this.workerError = false;

      // Workerからメッセージを受信したときの処理
      this.workerInstance.onmessage = (event) => {
        listenerCallback(event);
      };

      // 停止ボタン等で計測停止が指示されるまで待機する
      while (!this.hasStopOrder) {
        // Workerでエラーがあった場合は待機を終了
        if (this.workerError) {
          break;
        }

        // 一定時間スリープ
        await util.sleep(INTERVAL.WORKER_SLEEP);
      }

      if (this.workerInstance instanceof Worker) {
        // ワーカーに停止メッセージ送信
        this.workerInstance.postMessage({ type: WORKER_TASK.STOP, worker: workerType, isTestMode: this.isTestMode });
      } else {
        console.warn('workerInstance not exist');
      }

      // ポーリング停止
      this.isPolling = false;
    },

    /**
     * 計測データ取得処理の停止
     */
    async stopFetchingMeasurement() {
      this.hasStopOrder = true;
      this.isPolling = false;
      // スリープ分待ち、Workerの停止と直前の実施していた処理を終了させる
      await util.sleep(INTERVAL.WAIT_TIME);
      // 再表示のためにクリアする
      this.measurementUuid = '';
      this.clearMeasuredData();
    },

    /**
     * funcValuesの表示値を翻訳する
     * @param {object} funcValues 計測データの最大値・最小値・平均値を持つオブジェクト
     * @returns {object} 表示用の値を翻訳したfuncValues
     */
    translateFuncValues(funcValues) {
      const result = {};

      for (const did of Object.keys(funcValues)) {
        const funcVal = funcValues[did];
        const conversionInfo = this.conversionList[did];

        // decExStrの場合、minDispとmaxDispを翻訳する
        if (conversionInfo.data_type === 'decExStr') {
          const [systemName, itemId] = splitSystemAndItemId(did);
          const region = individualDiagnose.getRegion(this, this.individualMenus, this.projectDetails, this.measurementUuid, systemName);

          let translatedMax = getTranslateValue(
            this, itemId, funcVal.maxDisp, conversionInfo, region, systemName
          );
          translatedMax = translatedMax || fixNumberWithDecimal(funcVal.max, conversionInfo.digit_after_decimal);
          let translatedMin = getTranslateValue(
            this, itemId, funcVal.minDisp, conversionInfo, region, systemName
          );
          translatedMin = translatedMin || fixNumberWithDecimal(funcVal.min, conversionInfo.digit_after_decimal);

          result[did] = {
            ...funcVal, maxDisp: translatedMax, minDisp: translatedMin
          };
        } else {
          // それ以外はそのまま保持
          result[did] = funcVal;
        }
      }
      return result;
    },

    /**
     * iscpDownstreamWorkerからのメッセージ受信処理
     * @param {object} event
     */
    iscpDownstreamWorkerListener(event) {
      const data = event.data;

      // エラー受信の場合
      if (data.type === WORKER_EVENT.START_ERROR || data.type === WORKER_EVENT.RUNTIME_ERROR) {
        // Workerでエラーあり
        this.workerError = true;

        this.$emit("dm-err-notify", this.$t("individual.error_data_monitor_fetch"), false);
        if (this.isActiveTest) {
          // アクティブテストの場合、個別のエラーハンドリングを行うため、ここでデータモニタの停止やエラーダイアログ表示しない
          return;
        }

        // データモニター停止、エラーダイアログ表示
        if (data.type === WORKER_EVENT.START_ERROR) {
          // 計測開始前のため'errorTerminate'として停止させる
          this.stopMonitorErrorTerminate();
          this.showCommonError(ERR_MSG_KEY.FETCH, true);
        } else {
          this.stopMonitor();
          this.showCommonError(ERR_MSG_KEY.FETCH, false);
        }

      }
      // 正常受信(基準時刻)の場合
      else if (data.type === WORKER_EVENT.BASE_TIME) {
        // 基準時刻を設定
        this.baseTime = Math.floor(data.baseTime);
        this.baseTimeOffset = Math.floor(data.offset);
        // Intdashとの接続完了フラグ更新、ローディングを消す。baseTimeがないと後続処理で不都合があるため、基準時刻の受信をもって接続完了とする
        this.isConnected = true;
        // 計測時間確認用のタイマーを、現在時刻で開始
        this.checkMeasuredTime(new Date());
      }
      // 正常受信(Chunkデータ)の場合
      else if (data.type === WORKER_EVENT.CHUNK_DATA) {
        // 変換＆画面描画
        this.dataMonitorList = this.modifyChunkData(data.chunkData); // HEV系AT対応
        this.funcValues = this.translateFuncValues(data.funcValues);
        this.notifyDataTotalCount(data.originTotalCount);
      }
      // ワーカー停止の場合
      else if (data.type === WORKER_EVENT.STOPPED) {
        // Workerの停止処理が完了したら、Worker自体を終了する
        this.workerInstance.terminate();
        this.workerInstance = {};
        // ボタンの活性・非活性変更
        this.$emit("toggle-execute", false);
      }
    },

    /**
     * HEV系AT対応：AT駆動中以外の区間のチャンクデータconvertedDataを空文字にして返す
     * @param {object} chunkData 受信データ
     * @returns {object} 修正済みチャンクデータ
     */
    modifyChunkData(chunkData) {
      const modifiedChunkData = chunkData;

      /**
       * atInfo内のidと一致している場合、値を変更する
       * @param {*} self 呼び出し元this
       * @param {*} preData 変換対象データ
       * @param {*} conversionFunc 変換方法
       * @returns 変換データ
       */
      const modifyData = (self, preData, conversionFunc) => {
        let modifiedData = preData.convertedData;
        for (const item of self.atInfo) {
          if (item.id === getNoSystemItemId(preData.data_monitor_id)) {
            modifiedData = conversionFunc(self, preData.convertedData);
          }
        }
        return modifiedData;
      };

      // HEV系ATでない、またはAT駆動中の場合はそのまま返す
      if (!this.isHev || this.isActiveTestDriving) return chunkData;
      // 上記以外
      for (let i = 0; i < modifiedChunkData.length; i++) {
        for (let j = 0; j < modifiedChunkData[i].length; j++) {
          modifiedChunkData[i][j].convertedData = modifyData(
            this,
            modifiedChunkData[i][j],
            () => { return ""; }
          );
        }
      }
      return modifiedChunkData;
    },

    /**
     * RestfulWorkerからのメッセージ受信処理
     * @param {object} event Workerから通知されるイベント
     */
    restfulWorkerListener(event) {
      const data = event.data;

      // エラー受信の場合
      if ([WORKER_EVENT.START_ERROR, WORKER_EVENT.RUNTIME_ERROR, WORKER_EVENT.MEASUREMENT_NOT_FOUND].includes(data.type)) {
        this.isPolling = false;
        this.processing = false;
        // Workerでエラーあり
        this.workerError = true;

        // エラーダイアログ表示
        if (data.type === WORKER_EVENT.START_ERROR) {
          // 再生データ取得前にエラーのため、データを初期化
          this.stopFetchingMeasurement();
          this.showCommonError(ERR_MSG_KEY.FETCH, true);
        } else if (data.type === WORKER_EVENT.MEASUREMENT_NOT_FOUND) {
          // 再生データ取得前にエラーのため、データを初期化。404エラーはinfoダイアログを表示。
          this.stopFetchingMeasurement();
          alert.showInfo(this, null, this.$t("individual.error_data_monitor_data_not_exist"), () => this.$emit("minimum"));
          return;
        } else {
          this.showCommonError(ERR_MSG_KEY.FETCH, false);
        }
      }
      else if (data.type === WORKER_EVENT.MEASUREMENT_INFO) {
        // 基準時刻を設定
        this.baseTime = data.measurement.basetime;
        this.baseTimeOffset = data.measurement.offset;

        // マーカー設定。タグのsharedがtrueであるか、userIdが自分の場合は表示可能なマーカー
        this.markerInfo = data.measurement.markers.filter(marker => {
          const shared = marker.tag?.shared;
          const userId = marker.tag?.userId;
          if (typeof shared === 'string') {
            return shared.toLowerCase() === 'true' || userId === this.userProperty.userId;
          }
          return shared || userId === this.userProperty.userId;
        });
        // intdashからの取得の場合総データポイント数。S3からの取得の場合は総ファイル数。
        this.receivedDataPoints = data.measurement.received_data_points;
      }
      else if (data.type === WORKER_EVENT.CHUNK_DATA) {
        if (data.chunkData.length === 0 || (!this.inExecution && !this.inPlaying)) {
          this.hasStopOrder = true;
        }
        // 変換＆画面描画
        this.dataMonitorList = this.modifyRecollectedChunkData(data.chunkData); // HEV系AT対応
        this.funcValues = this.translateFuncValues(data.funcValues);
        this.escapedDataPoints = data.escapedDataPoints;
        this.$emit("update-total-count", data.originTotalCount);

        if (this.indexedDbManager && this.indexedDbManager.isAvailable()) {
          this.addIndexedDB(`${data.num}_${DATA_POINT_STORE.ID_TYPE.SHOW_DATA}`, this.dataMonitorList);
        }
      }
      // 表示に使用しない間引いたデータの場合
      else if (data.type === WORKER_EVENT.DECIMATED_DATA) {
        const d = this.modifyRecollectedChunkData(data.chunkData); // HEV系AT対応
        if (this.indexedDbManager && this.indexedDbManager.isAvailable()) {
          this.addIndexedDB(`${data.num}_${DATA_POINT_STORE.ID_TYPE.DECIMATED_DATA}`, d);
        }
      }
      else if (data.type === WORKER_EVENT.SUCCESS_END) {
        this.hasStopOrder = true;
        // ワーカーの処理正常終了時、全データ取得完了として総データポイント数を通知する
        this.$emit("update-total-count", this.receivedDataPoints);
      }
      // ワーカー停止の場合
      else if (data.type === WORKER_EVENT.STOPPED) {
        // Workerの停止処理が完了したら、Worker自体を終了する
        this.workerInstance.terminate();
        this.workerInstance = {};
      }
    },

    /**
     * HEV系AT対応：AT駆動中以外の区間のチャンクデータconvertedDataを空文字にして返す
     * @param {object} chunkData 受信データ
     * @returns {object} 修正済みチャンクデータ
     */
    modifyRecollectedChunkData(chunkData) {
      // 対象機能でない場合は、そのまま返す
      if (!this.isHev) return chunkData;

      // 計測時間がAT駆動中区間内かどうか返す
      const judgeIsAtDriving = (self, time) => {
        let flg = false;
        for (const interval of self.atOperatingStatusList) {
          if (
            interval.startTime <= (time - self.baseTime) * 1000 &&
            (time - self.baseTime) * 1000 <= interval.stopTime
          ) {
            flg = true;
            break;
          }
        }
        return flg;
      };

      const modifiedChunkData = chunkData;
      for (let i = 0; i < chunkData.length; i++) {
        for (let j = 0; j < chunkData[i].length; j++) {
          for (const item of this.atInfo) {
            if (
              getNoSystemItemId(modifiedChunkData[i][j].data_monitor_id) === item.id &&
              !judgeIsAtDriving(this, chunkData[i][j].time)
            ) {
              modifiedChunkData[i][j].convertedData = "";
            }
          }
        }
      }

      if (Object.keys(this.conversionList).length === this.atInfo.length) {
        // IO/RoutineControl項目のみが計測対象の場合、非駆動時もグラフ線が表示されるため、空のデータポイントを追加
        // チャンクの最初と最後のタイムスタンプの間である、駆動開始/終了タイムスタンプについて追加
        let addTimestamps = [];
        for (const element of this.atOperatingStatusList) {
          addTimestamps.push(element.startTime, element.stopTime);
        }
        addTimestamps = addTimestamps.filter(timestamp =>
          (modifiedChunkData[0][0].time - this.baseTime) * 1000 <= timestamp &&
          timestamp <= (modifiedChunkData.at(-1).at(-1).time - this.baseTime) * 1000 &&
          timestamp !== Infinity
        );
        for (const timestamp of addTimestamps) {
          for (const item of this.atInfo) {
            modifiedChunkData.push([{
              data_monitor_id: setSystem(this.systemId, item.id),
              convertedData: "",
              time: Math.floor(timestamp / 1000) + this.baseTime
            }]);
          }
        }
        // ソート
        modifiedChunkData.sort((a, b) =>
          a.at(-1).time - b.at(-1).time
        );
      }
      return modifiedChunkData;
    },

    /**
     * indexedDBにデータポイントを追加
     * @param {string} dbId indexedDBに登録するレコードのID
     * @param {array} data 登録するデータ
     */
    async addIndexedDB(dbId, data) {
      const addData = {};
      for (const chunk of data) {
        for (const convertedModel of chunk) {
          const id = convertedModel.data_monitor_id;
          // 変換済みデータ、計測時間を計測データリストに追加(convertedData相当の作成)
          if (!(id in addData)) {
            addData[id] = [];
          }
          // 節約のため配列へ置き換えて、csvで不要なものは載せない
          addData[id].push([convertedModel.time, convertedModel.convertedData]);
        }
      }

      this.indexedDbManager.asyncAdd(STORE_NAME.DATA_POINT, { id: dbId, d: addData }) // 待たない
        .catch(((e) => {
          console.error(e);
          this.showCommonError(ERR_MSG_KEY.FETCH, false);
        }));
    },

    /**
     * 計測時間確認処理
     * 計測APIが最大計測時間を超えたら停止
     * @param {*} startTime タイマーの開始日時
     */
    checkMeasuredTime(startTime) {
      const startDate = !startTime ? new Date() : startTime;
      this.timerId = setInterval(() => {
        if (this.hasStopOrder) {
          clearInterval(this.timerId);
          return;
        }

        const endTime = new Date();
        const measurementTime = endTime.getTime() - startDate.getTime();
        if (measurementTime >= MAX_TIME) {
          clearInterval(this.timerId);
          this.isPolling = false;

          this.$emit("dm-err-notify", this.$t("individual.alert_data_monitor_data_limit_exceeded"), true);
          if (this.isActiveTest) {
            // アクティブテストの場合、個別のエラーハンドリングを行うため、ここでデータモニタの停止やエラーダイアログ表示しない
            return;
          }

          // 計測API停止
          this.stopMonitor();
          alert.showWarning(
            this,
            null,
            this.$t("individual.alert_data_monitor_data_limit_exceeded"),
            () => { }
          );
        }
      }, INTERVAL.WATCH_TIMER);
    },

    /**
     * その他のエラー
     * @param {string} action エラーメッセージの種別
     * @param {boolean} isBack 信号線選択画面に戻る場合はtrueを指定
     */
    showCommonError(action, isBack) {
      let messageKey;
      switch (action) {
        // 操作時のエラー
        case ERR_MSG_KEY.INIT:
          messageKey = "individual.error_Initialize_communication_error";
          break;
        case ERR_MSG_KEY.MEASURE_START:
          messageKey = "individual.error_data_monitor_measure_start";
          break;
        case ERR_MSG_KEY.MEASURE_STOP:
          messageKey = "individual.error_data_monitor_measure_stop";
          break;
        case ERR_MSG_KEY.INFO_GET:
          messageKey = "individual.error_data_monitor_info_get";
          break;
        case ERR_MSG_KEY.MEASUREMENT_LIMIT:
          messageKey = "individual.error_data_monitor_measurement_count_limit";
          break;

        // 以下はIntadashサーバーの接続断時のエラー
        case ERR_MSG_KEY.INTDASH:
          messageKey = "individual.error_data_monitor_intdash";
          break;
        case ERR_MSG_KEY.REQUESTS_START:
          messageKey = "individual.error_data_monitor_requests_start";
          break;
        case ERR_MSG_KEY.FETCH:
          messageKey = "individual.error_data_monitor_fetch";
          break;
        case ERR_MSG_KEY.REQUESTS_EXECUTION:
          messageKey = "individual.error_data_monitor_requests_execution";
          break;

        // 一般的な通信エラー
        case ERR_MSG_KEY.COMMUNICATION:
        default:
          messageKey = "individual.error_communication_error";
          break;
      }

      if (!this.isError) {
        this.isError = true;
        alert.showError(this, null, this.$t(messageKey), () => { isBack ? this.back() : this.editFlg(); });
      }
    },

    /**
     * 計測を停止して項目選択画面に戻る
     */
    back() {
      this.isError = false;
      this.$emit("toggle-execute", false);
      this.$emit("toggle-show-select", true);
    },

    /**
     * エラーフラグをOFFにする
     */
    editFlg() {
      this.isError = false;
    },

    /**************** 計測API処理 ***************/

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      if (this.signout) {
        return;
      }

      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => {
          self;
          this.$emit("toggle-execute", false);
          this.logout(this);
        },
        // セッションリセット処理のコールバック
        (self) => {
          self;
          this.$emit("toggle-execute", false);
          this.resetSession(this);
        },
        // その他エラー発生のコールバック
        (result) => {
          result;
          this.showCommonError(ERR_MSG_KEY.COMMUNICATION, true);
        }
      );
    },

    /**
     * intdashアクセス情報取得処理
     */
    async getIntdashAccess() {
      let operationScreen = null;
      let queryParam = null;
      if (this.dataMonitorFunctionSettings.isPlay()) {
        // 再生画面からの呼び出し時はScreen-Nameをヘッダーに付与する
        operationScreen = diagnose.operationFrom(this.$router.currentRoute.value.name);
        queryParam = { type: "playback", region_info: this.cachedProjectDataSource };
      } else {
        queryParam = { type: "data_monitor", net_app_id: this.netAppId };
      }

      try {
        const response = await individualDiagnose.syncApi(
          this,
          // GET API 呼び出しのコールバック
          async () => {
            return await api.getParamCall(
              config.DMAT,
              "/intdashAccessInfo",
              queryParam,
              operationScreen
            );
          },
          // エラー発生のコールバック
          (errorType) => {
            logWrapper.log('getIntdashAccess() syncApi() error: ', JSON.stringify(errorType));
            if (errorType === ERR_MSG_KEY.INTDASH) {
              this.showCommonError(ERR_MSG_KEY.INTDASH, true);
            } else {
              this.showCommonError(ERR_MSG_KEY.MEASURE_START, true);
            }
          },
          // GET API レスポンスチェックのコールバック
          (getApiResponse) => {
            // intdashアクセス情報正常が入っていれば成功とする。ただし再生画面ではedgeIdは返されない
            if (getApiResponse &&
              getApiResponse.data &&
              getApiResponse.data.api_token &&
              (this.dataMonitorFunctionSettings.isPlay() || getApiResponse.data.edge_id) &&
              getApiResponse.data.expires_in &&
              getApiResponse.data.endpoint) {
              return "";
            } else if (getApiResponse.status !== 200 && getApiResponse.data.code === error.DMAT_BACKEND_ACCESS_TO_INTDASH_FAILED) {
              return ERR_MSG_KEY.INTDASH;
            } else {
              return config.INDIVIDUAL_API_CALL_ERROR_GET_INVALID_RESPONSE;
            }
          },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => {
            this.validateErrorResult(result);
          }
        );
        if (response &&
          response.data &&
          response.data.api_token &&
          (this.dataMonitorFunctionSettings.isPlay() || response.data.edge_id) &&
          response.data.expires_in &&
          response.data.endpoint) {
          // intdashアクセス情報を格納
          this.apiToken = response.data.api_token;
          this.edgeId = response.data.edge_id;
          this.tkExpire = response.data.expires_in;
          this.endpoint = response.data.endpoint;
        }
      } catch (e) {
        this.showCommonError(ERR_MSG_KEY.MEASURE_START, true);
      }
    },

    /**
     * データモニタ変換情報取得処理
     * @param {object} targetDidListBySystem システムごとの選択項目
     * @param {string} projectId プロジェクトのID（再生画面でのみ使用）
     * @param {number} registeredAt 計測登録日時のepoch秒（再生画面でのみ使用）
     * @returns {array} conversionInfo データモニタ変換情報
     */
    async getMonitorConversion(targetDidListBySystem, projectId = null, registeredAt = null) {
      function getQueryParam(self, targetDidList) {
        let queryParam = null;
        if (self.dataMonitorFunctionSettings.isPlay()) {
          queryParam = {
            type: "playback", target_did_list: targetDidList, project_id: projectId, measured_id: registeredAt, region_info: self.cachedProjectDataSource
          };
        } else {
          queryParam = {
            type: "data_monitor", target_did_list: targetDidList, net_app_id: self.netAppId
          };
        }
        return queryParam;
      }

      // 返却する配列
      const returnVal = {};
      const systems = Object.keys(targetDidListBySystem);

      try {
        // 再生画面からの呼び出し時はScreen-Nameをヘッダーに付与する
        const operationScreen = this.dataMonitorFunctionSettings.isPlay() ? diagnose.operationFrom(this.$router.currentRoute.value.name) : null;

        // API取得用配列分並列処理。システムごとのPromise.allを束ねた配列
        const promiseAlls = systems.map(systemName => {
          // 各システムの100項目ごとの変換情報を、並列で取得するPromise.all
          return Promise.all(
            targetDidListBySystem[systemName].map(did => {
              return api.getParamCall(
                config.DMAT,
                `/systems/${systemName}/monitorConversion`,
                getQueryParam(this, did),
                operationScreen
              );
            })
          );
        });

        // システムことのPromise.allをまとめて実行し、システムごとの結果を受け取る
        const resultsBySystem = await Promise.all(promiseAlls);
        this.didSizes = {};

        resultsBySystem.forEach((apiResults, index) => {
          const includeError = apiResults.some((result) => result instanceof Error);
          if (includeError) {
            throw new Error('getMonitorConversion() getParamCall() error: ', { cause: JSON.stringify(apiResults) });
          }

          // APIの結果に、conversion_listがないレスポンスが一つでもあったらエラーとする
          const includeConversionList = apiResults.every((result) => result?.data?.conversion_list !== undefined);

          if (includeConversionList) {
            // システム名をdidに付与して、結果を戻り値の変数に詰める
            const conversionList = apiResults.map((result) => result.data.conversion_list).flat();
            conversionList.forEach(convData => returnVal[setSystem(systems[index], convData.did)] = convData.info);

            apiResults.forEach((result) => {
              // システム名をdidに付与
              const didSizesWithSystem = Object.keys(result.data.did_sizes).reduce((accumulator, currentKey) => {
                return { ...accumulator, [setSystem(systems[index], currentKey)]: result.data.did_sizes[currentKey] };
              }, {});
              this.didSizes = Object.assign(this.didSizes, didSizesWithSystem);
            });
          } else {
            throw new Error('getMonitorConversion() getParamCall() error: ', { cause: config.INDIVIDUAL_API_CALL_ERROR_GET_INVALID_RESPONSE });
          }
        });

        // HEV系AT対応：IO/RoutineControl対象項目を追加
        const systemId = individualDiagnose.getCurrentSystemId(this) ?? this.systemId;
        const hevAddKeys = [];
        if (this.isHev) {
          // 最小の表示順序を取得
          let minimumDisplayOrder = 0;
          for (const item in Object.keys(returnVal)) {
            minimumDisplayOrder = minimumDisplayOrder > returnVal[item] ?
              returnVal[item].monitor_display_order : minimumDisplayOrder;
          }
          // Phase6か否かのフラグをdidSizesに追加
          this.didSizes = Object.assign(this.didSizes, {
            atInfo: [],
            isPhase6: isPhase6At(
              this.selectedAtFunc["active_test_id"]?.slice(0, 3) ||
              this.atInfo[0].id.split("_")[3].slice(0, 3)
            )
          });

          for (const element of this.atInfo) {
            // didSizeに追加
            const idSuffixAdded = `${setSystem(systemId, getDid(element.id))}_${element.type}`;
            this.didSizes = Object.assign(this.didSizes, {
              [idSuffixAdded]: Number(element.id.split("_")[1])
            });
            this.didSizes.atInfo.push({
              id: idSuffixAdded,
              type: element.type
            });
            // conversionInfo追加
            returnVal[setSystem(systemId, element.id)] = {
              byte_or_bit: "byte",
              start_byte_position: Number(element.id.split("_")[1]),
              byte_size: 1,
              start_bit_position: 0,
              bit_size: 0,
              signature: "unsigned",
              data_type: "HEX",
              scaling_bit: 0,
              offset: 0,
              unit: "unitId_0",
              data_monitor_display: true,
              data_monitor_display_analysis: false,
              ffd_display: false,
              ffd_display_analysis: false,
              ffd_control_history_display: false,
              ffd_control_history_display_analysis: false,
              digit_after_decimal: 0,
              graph_range_display: {
                min: 0,
                max: 0
              },
              monitor_display_order: minimumDisplayOrder - 1,
              little_endian: false,
              time_format: {
                exist: false
              },
              unit_id: "unitId_0",
              unit_conversion: {},
              active_test_id: element.id.split("_")[3].slice(0, -2),
            };
            // didConversionMap追加用
            hevAddKeys.push(setSystem(systemId, element.id));
          }
        }

        this.didConversionMap = {};
        // 変換処理で変換情報を早く引くためのオブジェクトを別途作成。1つのDIDに複数の信号線があるため、DIDをキーに配列で保持
        Object.keys(returnVal).forEach((itemId, index) => {
          let did = getDid(itemId);
          if (hevAddKeys.includes(itemId)) {
            // IO/Routine Control 対象の場合、独自キーを設定
            did = `${getDid(itemId)}_${this.atInfo[0].type}`;
          }
          if (this.didConversionMap[did]) {
            this.didConversionMap[did].push(index);
          } else {
            this.didConversionMap[did] = [index];
          }
        });
      } catch (e) {
        logWrapper.log(e.messeage, e.cause || '');
        this.showCommonError(ERR_MSG_KEY.COMMUNICATION, true);
      }
      // APIのresponseをまとめた返却値を返却
      return returnVal;
    },

    /**
     * 選択項目を最大一括取得件数で切り出す処理
     * @param {array} selected_did_list 選択項目
     * @returns targetDidListArr 最大一括取得件数で切り出した選択項目の配列
     */
    getDidListArr(selected_did_list) {
      // 切り出し結果の配列
      const targetDidListArr = [];
      // 最大一括取得件数
      const MAX_ITEMS = 100;

      let targetDidList = [];
      // 選択された項目分ループ処理
      for (let i = 0; i < selected_did_list.length; i++) {
        // selected_did_list[i]を格納
        targetDidList.push("\"" + selected_did_list[i].id + "\"");
        // 余りが最大一括取得件数 - 1の場合、もしくは最後のループの場合
        if (i % MAX_ITEMS === MAX_ITEMS - 1 || i === selected_did_list.length - 1) {
          // 今までの格納分をAPI取得用配列に格納
          targetDidListArr.push("[" + targetDidList.toString() + "]");
          targetDidList = [];
        }
      }

      return targetDidListArr;
    },

    /**
     * HEV系ATのDM１行目表示対象が含まれる場合は除外して返す
     * @param {*} list
     */
    escapeHevTarget(list) {
      if (!this.isHev) return list;
      return list.filter(item => {
        let res = true;
        for (const element of this.atInfo) {
          if (getNoSystemItemId(item) === element.id) {
            res = false;
          }
        }
        return res;
      });
    },

    /**
     * 計測開始API Polling処理
     * @param {string} token id_token
     * @param {array} selected_did_list 選択項目List
     */
    async measureStart(token, selected_did_list) {
      // HEV系ATの場合、1行目表示対象を除外
      const itemsBySystem = getItemsBySystem(this.escapeHevTarget(selected_did_list));
      const monitor_item_list = {};
      for (const key in itemsBySystem) {
        monitor_item_list[key] = itemsBySystem[key].map(item => item.id);
      }

      const systemNames = Object.keys(itemsBySystem);
      if (this.isHev) {
        // HEV系ATの場合、systemNames.length = 0となる可能性があるため、追加
        let target = "";
        for (const item of this.atInfo) {
          target = selected_did_list.find(element => getNoSystemItemId(element) === item.id);
        }
        if (target !== "" && systemNames.findIndex(systemName => systemName === getSystem(target)) === -1) {
          systemNames.push(getSystem(target));
        }
      }

      this.hasStopOrder = false;
      // Polling loop Count
      let loopCnt = 0;
      // ネットワークエラーが発生した回数
      let networkErrorCount = 0;
      // is_active_testのフラグ作成
      const isATTest = !this.isDataMonitor;
      const response = individualDiagnose.pollingApi(
        // self
        this,
        // postApiCallback
        async () => {
          // API取得開始 FLG 変更
          this.isGetApi = true;
          return await api.postCall(
            config.DMAT,
            "/netApps/" +
            this.netAppId +
            "/dataMonitor",
            {
              id_token: token.id_token,
              edge_id: this.edgeId,
              measurement_id: this.measurementUuid,
              monitor_item_list: monitor_item_list,
              type: "start",
              // アクティブテストの場合、常にfalseとする
              is_special: this.isSpecial && !isATTest,
              is_active_test: isATTest,
              is_persist: this.dataMonitorFunctionSettings.isPersist,
              system_name: systemNames
            }
          );
        },
        // getApiCallback
        async (postApiResponse) => {
          // postApi取得結果が正常の場合
          if (this.isCompleted) {
            // sleepを入れてgetの周期を遅らせる
            await util.sleep(INTERVAL.MEASURE_SLEEP);
          }
          const getApiResponse = await api.getCall(
            config.DMAT,
            "/netApps/" +
            this.netAppId +
            "/dataMonitor/requests/" +
            postApiResponse.data.request_id
          );
          const isNetworkError = (
            getApiResponse.data.code === CommonError.FRONT_DISCONNECT_NETWORK ||
            getApiResponse.data.code === CommonError.FRONT_SERVER_NO_RESPONCE
          );
          if (isNetworkError && (networkErrorCount + 1) <= RETRY.MEASURE_POLLING_NETWORK_ERROR) {
            // ネットワーク切断時のエラーは、1分間は正常なレスポンスに置き換えてポーリングを続行させる
            networkErrorCount++;
            getApiResponse.data.code = null;
            getApiResponse.data.status = this.isCompleted ? config.STATUS_DMAT_COMPLETED : config.STATUS_DMAT_IN_PROGRESS;
            getApiResponse.status = 200;
          } else {
            networkErrorCount = 0;
          }
          return getApiResponse;
        },
        // errorCallback
        (errorType) => {
          logWrapper.log('measureStart() pollingApi() error: ', JSON.stringify(errorType));
          this.isPolling = false;
          this.checkProcessing = false;
          // 計測停止 FLG変更
          this.hasStopOrder = true;
          // 開始前
          if (!this.isCompleted) {
            if (errorType === CommonError.TOO_MANY_REQUESTS_EXCEPTION) {
              this.showCommonError(ERR_MSG_KEY.MEASUREMENT_LIMIT, true);
            } else {
              this.showCommonError(ERR_MSG_KEY.MEASURE_START, true);
            }
          }
          // 開始後
          else {
            this.showCommonError(ERR_MSG_KEY.REQUESTS_EXECUTION, false);
          }
        },
        // checkPostApiResponseCallback
        (postApiResponse) => {
          if (!postApiResponse || !postApiResponse.data) {
            return POLLING_ERROR_POST_INVALID_RESPONSE;
          }
          if (postApiResponse.data.async_code === CommonError.TOO_MANY_REQUESTS_EXCEPTION) {
            // 既に3200台が計測中の場合のエラー
            return CommonError.TOO_MANY_REQUESTS_EXCEPTION;
          }
          return postApiResponse.data.request_id
            ? ""
            : POLLING_ERROR_POST_INVALID_RESPONSE;
        },
        // checkGetApiResponseCallback
        (getApiResponse) => {
          if (this.signout) {
            // サインアウト後は後続処理を行ってもエラーとなるため処理終了
            return config.INDIVIDUAL_POLLING_RESULT_FINISH;
          }
          this.checkProcessing = true;
          let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
          result; // to avoid warning
          if (
            getApiResponse &&
            getApiResponse.data &&
            getApiResponse.data.status
          ) {
            switch (getApiResponse.data.status) {
              case config.STATUS_DMAT_COMPLETED: {
                let responseTypeNg = false;
                // 通信エラーチェック
                if (getApiResponse.data.user_data && Array.isArray(getApiResponse.data.user_data)) {
                  for (const userData of getApiResponse.data.user_data) {
                    if (userData.res_type !== 'ok') {
                      responseTypeNg = true;
                      break;
                    }
                  }
                }

                // 正常時
                if (!util.parseBoolean(getApiResponse.data.int_dash_sending_error) && !responseTypeNg) {
                  // Completed 初回処理
                  if (!this.isCompleted) {
                    // ボタンの活性・非活性変更
                    this.$emit("toggle-execute", true);
                    // Completed FLG変更
                    this.isCompleted = true;

                    // 利用カウントアップ処理
                    if (this.isDataMonitor) {
                      systemNames.forEach((systemName) => {
                        this.usageCountUp({
                          type: this.isSpecial ? config.USAGE_COUNT_SP_DM : config.USAGE_COUNT_DM,
                          systemName: systemName,
                        });
                      });
                    }
                  }

                  // リトライ回数判定
                  loopCnt++;
                  // API取得回数 >= リトライ回数の場合
                  if (
                    loopCnt >= RETRY.MEASURE_START ||
                    !this.isPolling ||
                    this.hasStopOrder
                  ) {
                    // Pollingを正常終了
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    // Polling FLG変更
                    this.isPolling = false;
                    // checkGetApiリトライ中 FLG変更
                    this.checkProcessing = false;
                    // 計測停止 FLG変更
                    this.hasStopOrder = true;
                    // 上記以外
                  } else {
                    // Pollingを続ける
                    result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                    loopCnt = 0;
                  }

                }
                // CC->Intdash通信エラー時、またはCC->ECU SIM通信エラー時
                else {
                  if (!this.isCompleted) {
                    // 計測開始が完了する前のエラーの場合、計測停止APIを呼ばずに終了する
                    // ActiveTestは個別のエラーハンドリングで停止を行うため、dm-err-notifyを投げる前に終了する
                    this.hasStopOrder = true;
                    this.isPolling = false;
                    this.checkProcessing = false;
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    this.showCommonError(ERR_MSG_KEY.COMMUNICATION, true);
                    break;
                  }

                  // 計測中のCC->Intdash送信エラー発生の場合
                  if (util.parseBoolean(getApiResponse.data.int_dash_sending_error)) {
                    this.$emit("dm-err-notify", this.$t("individual.error_data_monitor_requests_execution"), false);
                    !this.isActiveTest && this.showCommonError(ERR_MSG_KEY.REQUESTS_EXECUTION, false);
                  } else {
                    this.$emit("dm-err-notify", this.$t("individual.error_communication_error"), false);
                    // 計測中の場合
                    if (this.isPolling) {
                      !this.isActiveTest && this.showCommonError(ERR_MSG_KEY.COMMUNICATION, false);
                    }
                    // 未計測の場合
                    else {
                      !this.isActiveTest && this.showCommonError(ERR_MSG_KEY.COMMUNICATION, true);
                    }
                  }

                  this.isPolling = false;
                  this.checkProcessing = false;

                  if (this.isActiveTest) {
                    // アクティブテストの場合、個別のエラーハンドリングを行うため、ここでデータモニタの停止やエラーダイアログ表示しない
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    break;
                  }

                  this.stopMonitor();
                }
                break;
              }
              case config.STATUS_DMAT_IN_PROGRESS: {
                if (!this.isCompleted) {
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;

                  // リトライ回数判定
                  loopCnt++;

                  if (loopCnt >= RETRY.MEASURE_START) {
                    this.isPolling = false;
                    this.checkProcessing = false;
                  }
                }
                break;
              }
              case config.STATUS_DMAT_FAILED: {
                if (getApiResponse.data.async_code === error.DMAT_CLOUD_CONNECTOR_ACCESS_TO_INTDASH_FAILED) {
                  this.isPolling = false;
                  this.checkProcessing = false;
                  this.stopMonitorErrorTerminate();
                  this.showCommonError(ERR_MSG_KEY.REQUESTS_START, true);
                }
                break;
              }
              default:
                this.isPolling = false;
                this.checkProcessing = false;
                break;
            }
          }
          return result;
        },
        // checkPostApiErrorResultCallback
        (result) => {
          // 計測停止 FLG変更
          this.hasStopOrder = true;
          this.isPolling = false;
          this.checkProcessing = false;
          this.validateErrorResult(result);
        },
        // checkGetApiErrorResultCallback
        () => {
          if (this.signout) {
            // サインアウト後は後続処理を行ってもエラーとなるため処理終了
            return;
          }
          this.isPolling = false;
          this.checkProcessing = false;
          // 開始処理自体は行われた後のため、取得でのエラー時は停止APIを呼ぶ。再生データを保存したいため、通常停止として呼び出す
          this.stopMonitor();
          this.showCommonError(ERR_MSG_KEY.FETCH, false);
        },
        RETRY.MEASURE_START,
        null,
        // 計測中はセッションタイムアウトさせない
        true
      );
      return response;
    },

    /**
     * データモニタ停止処理
     * @param {array} selected_did_list 選択項目List
     * @param {boolean} errorTerminateFlg errorTerminateFlgフラグ(trueの場合typeをerrorTerminateにする)
     * @param {string} activeTestId アクティブテストid
     */
    async measureStop(selected_did_list, errorTerminateFlg, activeTestId) {
      const targetDidListBySystem = getItemsBySystem(this.escapeHevTarget(selected_did_list));
      const systemNames = Object.keys(targetDidListBySystem);
      if (this.isHev) {
        // HEV系ATの場合、systemNames.length = 0となる可能性があるため、追加
        let target = "";
        for (const item of this.atInfo) {
          target = selected_did_list.find(element => getNoSystemItemId(element) === item.id);
        }
        if (target !== "" && systemNames.findIndex(systemName => systemName === getSystem(target)) === -1) {
          systemNames.push(getSystem(target));
        }
      }

      this.memoUuid = "";
      this.$emit("toggle-execute", false);
      this.hasStopOrder = true;
      // is_active_testのフラグ作成
      const isATTest = !this.isDataMonitor;
      // is_multipleのフラグ作成
      const isMultiple = this.dataMonitorFunctionSettings.isMultipleDataMonitor();
      const token = session.getToken();

      const monitorItemList = {};
      Object.keys(targetDidListBySystem).forEach(systemName => {
        if (isATTest) {
          monitorItemList[systemName] = targetDidListBySystem[systemName].map(item => ({
            id: item.id,
            highlight: this.highlightItems.includes(setSystem(systemName, item.id)),
            display_order: item.display_order
          }));
        } else {
          monitorItemList[systemName] = targetDidListBySystem[systemName].map(item => ({
            id: item.id,
            display_order: item.display_order
          }));
        }
      });

      // 停止処理と確認のポーリング処理をひとまとめに関数化
      const runStopAndCheck = async () => await individualDiagnose.pollingApi(
        // self
        this,
        // postApiCallback
        async () => {
          return await api.postCall(
            config.DMAT,
            "/netApps/" +
            this.netAppId +
            "/dataMonitor",
            {
              id_token: token.id_token,
              measurement_id: this.measurementUuid,
              monitor_item_list: monitorItemList,
              type: errorTerminateFlg ? "errorTerminate" : "stop",
              is_active_test: isATTest,
              is_multiple: isMultiple,
              // 停止処理の場合、アクティブテストの時でも特殊機能かどうかを設定する
              is_special: this.isSpecial,
              active_test_id: activeTestId,
              is_persist: this.dataMonitorFunctionSettings.isPersist,
              system_name: systemNames,
              registered_at: this.isHev ? this.baseTime : null
            }
          );
        },
        // getApiCallback
        async (postApiResponse) => {
          if (this.memoUuid === "" && postApiResponse.data.measured_id !== undefined) {
            this.memoUuid = postApiResponse.data.measured_id;
          }
          return await api.getCall(
            config.DMAT,
            "/netApps/" +
            this.netAppId +
            "/dataMonitor/requests/" +
            postApiResponse.data.request_id
          );
        },
        // errorCallback
        (errorType) => {
          logWrapper.log('measureStop() pollingApi() error: ', JSON.stringify(errorType));
          this.showCommonError(ERR_MSG_KEY.MEASURE_STOP, false);
        },
        // checkPostApiResponseCallback
        (postApiResponse) => {
          return postApiResponse &&
            postApiResponse.data &&
            postApiResponse.data.request_id
            ? ""
            : config.INDIVIDUAL_API_CALL_ERROR_POST_INVALID_RESPONSE;
        },
        // checkGetApiResponseCallback
        (getApiResponse) => {
          let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
          if (
            getApiResponse &&
            getApiResponse.data &&
            getApiResponse.data.status
          ) {
            switch (getApiResponse.data.status) {
              case config.STATUS_DMAT_COMPLETED:
                result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                // 通信エラーチェック
                if (getApiResponse.data.user_data && Array.isArray(getApiResponse.data.user_data)) {
                  for (const userData of getApiResponse.data.user_data) {
                    if (userData.res_type !== 'ok') {
                      // 通信エラー時
                      this.showCommonError(ERR_MSG_KEY.MEASURE_STOP, false);
                      break;
                    }
                  }
                }

                this.$emit("toggle-execute", false);
                break;

              case config.STATUS_DMAT_IN_PROGRESS:
                result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                break;

              case config.STATUS_DMAT_FAILED:
              default:
                this.$emit("toggle-execute", false);
                break;
            }
          }
          return result;
        },
        // checkPostApiErrorResultCallback
        (result) => {
          // 停止APIから異常なAPIレスポンス（ネットワークエラーなど）の場合はエラー状態を保持
          this.stopError = result;
        },
        // checkGetApiErrorResultCallback
        (result) => {
          // 停止確認APIから異常なAPIレスポンス（ネットワークエラーなど）の場合はエラー状態を保持
          this.stopError = result;
        },
        // pollingRetryCount
        RETRY.MEASURE_STOP,
        null,
        // 計測停止中はセッションタイムアウトさせない
        true
      );

      let stopRetryCount = 0;

      while (stopRetryCount <= RETRY.MEASURE_STOP_NETWORK_ERROR) {
        this.stopError = null;
        // 停止処理と確認のポーリング処理を実行
        await runStopAndCheck();
        if (!this.stopError) {
          // 停止時にエラーが無ければ終了
          break;
        }
        if (RETRY.MEASURE_STOP_NETWORK_ERROR <= stopRetryCount) {
          // リトライしてもエラーであれば、ダイアログを表示してログイン画面へ
          this.validateErrorResult(this.stopError);
          break;
        }
        stopRetryCount++;
        await util.sleep(INTERVAL.NETWORK_ERROR);
      }
    },

    setSignout() {
      this.signout = true;
    },

    // グラフ設定
    handleConfigureGraph() {
      this.$refs.data_monitor_execute.handleConfigureGraph();
    },

    /**
     * ズームリセット
     */
    handleResetZoom() {
      this.$refs.data_monitor_execute.handleResetZoom();
    },

    /** データモニタの停止 */
    execStopMonitor() {
      this.$emit('exec-stop-monitor');
    },

    /** 項目選択に戻る */
    backSelect() {
      this.$emit('back-select');

      if (this.isActiveTestDriving) {
        // 駆動中は戻るボタンが押下されたことを通知するのみ
        return;
      }

      this.unmountMeasure = true;
      // 1度描画してMeasureコンポーネントを確実に消す
      this.$nextTick(() => {
        this.unmountMeasure = false;
      });
    },

    /** 信号を選択していない場合、メモ、グラフ設定ボタンを消す */
    changeNoSignal(value) {
      this.$emit('change-no-signal', value);
    },

    // DM表示の値を変更する
    dmValueChange(target) {
      this.dataMonitorList = target;
    },

    /**
     * 汎用のマーカ情報の保存
     * @param {String} comment マーカー登録情報
     * @returns {boolean} 処理の成否
     */
    async externalSaveMarker(comment) {
      // データモニタ停止中の場合は、マーカ登録処理を実施しない
      if (!this.inExecution) return true;

      return await this.$refs.data_monitor_execute.externalSaveMarker(comment);
    },

    /**
     * 計測IDを取得する
     */
    getMeasurementId() {
      return this.measurementUuid;
    },
  },

  created() {
    // noop
  },

  mounted() {
    this.mountedLogic();
  },

  beforeMount() {
    // 計測データを初期化。変更が多いためdataに持たせたくなく、warningとなるためbeforeMountで初期化する
    this.dataMonitorList = [];
  },

  beforeUnmount() {
    // Worker停止に失敗していた場合、画面を離れる前にWorkerを終了する
    if (this.workerInstance instanceof Worker) {
      this.workerInstance.terminate();
      this.workerInstance = {};
    }
    if (this.indexedDbManager) {
      this.indexedDbManager.deleteDB();
    }
  }
};
